import { useAirtableQuery } from '@effects/useAirtableQuery';
import { APP_MESSAGES_QUERY_KEY, fetchAppMessages } from '@services/app-messages';
import { IAppMessage, IAppMessageCriteria } from '@typings';
import { matchAppMessages } from '@utils/app-message-matcher';
import { useMemo } from 'react';

/**
 * The `App_Messages` rows that apply to the given screen.
 *
 * Deliberately keys off `data` only: with an empty cache and a failed fetch `data` is undefined and
 * nothing renders (fail open), but a failed *background* refetch keeps the previously cached rows
 * (react-query 3 retains `data` when status becomes `error`), so a banner already on screen does
 * not vanish mid-edit.
 */
export const useAppMessages = (criteria: IAppMessageCriteria): IAppMessage[] => {
    const { data } = useAirtableQuery<IAppMessage[]>(APP_MESSAGES_QUERY_KEY, fetchAppMessages);
    const formsKey = criteria.forms.join(',');

    return useMemo(
        () => matchAppMessages(data ?? [], criteria),
        // Memoise on primitives (formsKey stands in for criteria.forms): the criteria object itself may be
        // rebuilt by the parent on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data, criteria.platform, criteria.route, formsKey, criteria.status, criteria.today]
    );
};

export default useAppMessages;
